import {
  AddFilled,
  ArrowClockwiseRegular,
  DeleteRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { LessonThemeResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { times } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { CurrentLessonCard } from '../CurrentLessonCard/CurrentLessonCard';
import {
  TeachingUnitEntry,
  TeachingUnitEntryType,
} from '../TeachingUnitEntry/TeachingUnitEntry';

export interface LessonContentLessonCardProps {
  isLoading: boolean;
  lessonContent: LessonThemeResponse[];
  onAddContentClick?(): void;
  onEditContentClick?(content: LessonThemeResponse): void;
  onDeleteContentClick?(content: LessonThemeResponse): void;
  onRefresh?(): void;
  disabled?: boolean;
}

export const LessonContentLessonCard: React.FC<
  LessonContentLessonCardProps
> = ({
  isLoading,
  lessonContent,
  onAddContentClick,
  onEditContentClick,
  onDeleteContentClick,
  onRefresh,
  disabled,
}) => {
  const { t } = useTranslation();
  return (
    <CurrentLessonCard
      refreshButton={
        !disabled && onRefresh
          ? {
              icon: <ArrowClockwiseRegular />,
              onClick: onRefresh,
              disabled: isLoading,
            }
          : undefined
      }
      title={t('classbook:lessoncontentlesson:header', { defaultValue: 'Unterrichtsinhalte' })}
      actionButton={
        <Button
          aria-label={t('classbook:lessoncontentlesson:lesson_new_label', { defaultValue: 'Öffnet Dialog zum Erstellen eines neuen Unterrichtsinhalt' })}
          disabled={disabled || isLoading}
          primaryTransparent
          icon={<AddFilled />}
          onClick={() => onAddContentClick?.()}
        >
          {t('classbook:lessoncontentlesson:lesson_new', { defaultValue: 'Neuer Unterrichtsinhalt' })}
        </Button>
      }
    >
      {isLoading &&
        times(2, index => (
          <TeachingUnitEntry
            key={`lesson-content-skeleton-${index}`}
            type={TeachingUnitEntryType.EducationalMaterial}
            headline=""
            loading
          />
        ))}
      {!isLoading &&
        lessonContent.map((content, index) => (
          <TeachingUnitEntry
            key={`lesson_content_${index}_${content.id}`}
            headline={content.method?.name ?? ''}
            subheadline={content.themeDescription ?? ''}
            type={TeachingUnitEntryType.EducationalMaterial}
            author={{
              id: content.createdBy?.id ?? '',
              displayName: content.createdBy?.displayName ?? '',
              mailAddress: content.createdBy?.mailAddress ?? '',
              firstName: '',
              lastName: '',
            }}
            onClick={
              !disabled ? () => onEditContentClick?.(content) : undefined
            }
            actions={
              !disabled
                ? [
                    {
                      icon: (
                        <EditRegular
                          aria-label={t('classbook:lessoncontentlesson:edit_label', { defaultValue: 'Unterrichtsinhalte bearbeiten, öffnet Dialog zum Bearbeiten des ausgewählten Unterrichtsinhalts' })}
                        />
                      ),
                      onClick: () => onEditContentClick?.(content),
                    },
                    {
                      icon: (
                        <DeleteRegular
                          aria-label={t('classbook:lessoncontentlesson:delete_label', { defaultValue: 'Unterrichtsinhalte löschen, öffnet Dialog zum Löschen des ausgewählten Unterrichtsinhalts' })}
                        />
                      ),
                      onClick: () => onDeleteContentClick?.(content),
                    },
                  ]
                : []
            }
          />
        ))}
    </CurrentLessonCard>
  );
};
